import React from 'react';
import './Achievements.css';

interface AchievementsProps {
  isOpen: boolean;
  onClose: () => void;
  score: number;
  coins: number;
  hasUsedGambling: boolean;
  gamblingWins: number;
  gamblingLosses: number;
  gamblingCoinsWon: number;
}

interface AchievementMeta {
  hasUsedGambling: boolean;
  gamblingWins: number;
  gamblingLosses: number;
  gamblingCoinsWon: number;
}

interface Achievement {
  id: string;
  name: string;
  description: string;
  condition: (score: number, coins: number, meta: AchievementMeta, allAchievements?: Achievement[]) => boolean;
  icon: string;
}

const achievements: Achievement[] = [
  {
    id: 'first_ascension',
    name: 'First Steps',
    description: 'Complete your first ascension',
    condition: (score) => score >= 1,
    icon: '🎯',
  },
  {
    id: 'score_10',
    name: 'Getting Started',
    description: 'Reach a score of 10',
    condition: (score) => score >= 10,
    icon: '⭐',
  },
  {
    id: 'score_50',
    name: 'Half Century',
    description: 'Reach a score of 50',
    condition: (score) => score >= 50,
    icon: '🌟',
  },
  {
    id: 'score_100',
    name: 'Century Club',
    description: 'Reach a score of 100',
    condition: (score) => score >= 100,
    icon: '💯',
  },
  {
    id: 'score_500',
    name: 'Master Pusher',
    description: 'Reach a score of 500',
    condition: (score) => score >= 500,
    icon: '🏆',
  },
  {
    id: 'coins_10',
    name: 'First Coins',
    description: 'Earn 10 coins',
    condition: (_, coins) => coins >= 10,
    icon: '🪙',
  },
  {
    id: 'coins_50',
    name: 'Coin Collector',
    description: 'Earn 50 coins',
    condition: (_, coins) => coins >= 50,
    icon: '💰',
  },
  {
    id: 'coins_100',
    name: 'Wealthy',
    description: 'Earn 100 coins',
    condition: (_, coins) => coins >= 100,
    icon: '💎',
  },
  {
    id: 'coins_500',
    name: 'Janis',
    description: 'Earn 500 coins',
    condition: (_, coins) => coins >= 500,
    icon: '💵',
  },
  {
    id: 'coins_1000',
    name: 'Janis Burnout',
    description: 'Earn 1000 coins',
    condition: (_, coins) => coins >= 1000,
    icon: '👑',
  },
  {
    id: 'coins_1024',
    name: 'Fucking Mārcis',
    description: 'Earn 1024 coins',
    condition: (_, coins) => coins >= 1024,
    icon: '🔥',
  },
  {
    id: 'gambling_access',
    name: 'Problem Gambler',
    description: 'Open the Gambling Addiction slot machine.',
    condition: (_, __, meta) => meta.hasUsedGambling,
    icon: '🎰',
  },
  {
    id: 'gambling_lose',
    name: 'House Always Wins',
    description: 'Lose at the Gambling Addiction slot machine.',
    condition: (_, __, meta) => meta.gamblingLosses >= 1,
    icon: '💸',
  },
  {
    id: 'gambling_win',
    name: 'Beginner\'s Luck',
    description: 'Win once at the Gambling Addiction slot machine.',
    condition: (_, __, meta) => meta.gamblingWins >= 1,
    icon: '🍀',
  },
  {
    id: 'gambling_win_200',
    name: 'Casino Whale',
    description: 'Win a total of 200 coins from the Gambling Addiction slot machine.',
    condition: (_, __, meta) => meta.gamblingCoinsWon >= 200,
    icon: '🐳',
  },
  {
    id: 'all_achievements',
    name: 'Top Pimpis',
    description: 'Unlock all other achievements',
    condition: (score, coins, meta, allAchievements) => {
      if (!allAchievements) return false;
      // Check all achievements except this one (the last one)
      const otherAchievements = allAchievements.slice(0, -1);
      return otherAchievements.every(ach => ach.condition(score, coins, meta, allAchievements));
    },
    icon: '👑',
  },
];

const Achievements: React.FC<AchievementsProps> = ({ isOpen, onClose, score, coins, hasUsedGambling, gamblingWins, gamblingLosses, gamblingCoinsWon }) => {
  if (!isOpen) return null;

  const meta: AchievementMeta = {
    hasUsedGambling,
    gamblingWins,
    gamblingLosses,
    gamblingCoinsWon,
  };

  const unlockedAchievements = achievements.filter(achievement => 
    achievement.condition(score, coins, meta, achievements)
  );
  const lockedAchievements = achievements.filter(achievement => 
    !achievement.condition(score, coins, meta, achievements)
  );

  return (
    <div className="achievements-overlay" onClick={onClose}>
      <div className="achievements-popup" onClick={(e) => e.stopPropagation()}>
        <button className="achievements-close-button" onClick={onClose}>×</button>
        
        <div className="achievements-header">
          <h2 className="achievements-title">🏆 Achievements</h2>
          <p className="achievements-subtitle">
            {unlockedAchievements.length} / {achievements.length} unlocked
          </p>
        </div>

        <div className="achievements-list">
          {unlockedAchievements.map(achievement => (
            <div key={achievement.id} className="achievement-item unlocked">
              <span className="achievement-icon">{achievement.icon}</span>
              <div className="achievement-info">
                <h3>{achievement.name}</h3>
                <p>{achievement.description}</p>
              </div>
              <span className="achievement-badge">✓</span>
            </div>
          ))}
          
          {lockedAchievements.map(achievement => (
            <div key={achievement.id} className="achievement-item locked">
              <span className="achievement-icon">🔒</span>
              <div className="achievement-info">
                <h3>???</h3>
                <p>{achievement.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Achievements;

